// src/components/AdvertisementBanner.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { getActiveAds, getAppSetting, Advertisement as RemoteAdvertisement } from '@/lib/admin-api';

interface Advertisement {
  id: string;
  title: string;
  content: string;
  link?: string;
  type: 'bbq' | 'school' | 'business';
  duration: number; // days to show
  startDate: string;
  active?: boolean; // Whether ad is active/visible
}

const DEFAULT_ADS: Advertisement[] = [
  {
    id: 'bbq-1',
    title: '🎮 BBQ Community - New Gaming Features!',
    content: 'Join BBQ Community for exclusive gaming tournaments, spades matches, and connect with fellow gamers!',
    link: 'https://bbq-community.com',
    type: 'bbq',
    duration: 30,
    startDate: new Date().toISOString(),
    active: true
  },
  {
    id: 'school-1',
    title: '📚 Spring Registration Open',
    content: 'Register for Spring semester classes now! Early registration ends January 31st.',
    link: '#',
    type: 'school',
    duration: 20,
    startDate: new Date().toISOString(),
    active: true
  },
  {
    id: 'business-1',
    title: '☕ Study Fuel: Campus Coffee 20% Off',
    content: 'Get 20% off all drinks with your student ID. Promo code: WHIPT20',
    link: '#',
    type: 'business',
    duration: 14,
    startDate: new Date().toISOString(),
    active: true
  }
];

const USER_ADS_KEY = 'whipt_ads_enabled';

const guessAdType = (title: string): Advertisement['type'] => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('bbq')) return 'bbq';
  if (lowerTitle.includes('school') || lowerTitle.includes('class')) return 'school';
  return 'business';
};

export function AdvertisementBanner() {
  const [globalAdsEnabled, setGlobalAdsEnabled] = useState(true);
  const [userAdsEnabled, setUserAdsEnabled] = useState(() => {
    const saved = localStorage.getItem(USER_ADS_KEY);
    return saved !== 'false';
  });
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [ads, setAds] = useState<Advertisement[]>([]);

  const adsAreVisible = globalAdsEnabled && userAdsEnabled && ads.length > 0;

  const loadAdsFromDatabase = useCallback(async () => {
    try {
      const [remoteAds, adsSetting] = await Promise.all([
        getActiveAds(),
        getAppSetting('ads_enabled')
      ]);

      if (adsSetting !== null) {
        setGlobalAdsEnabled(adsSetting !== 'false');
      }

      if (remoteAds.length > 0) {
        const normalized = remoteAds
          .filter((ad) => ad.active !== 0)
          .map((ad: RemoteAdvertisement): Advertisement => ({
            id: ad.id,
            title: ad.title,
            content: ad.description || '',
            link: ad.link,
            type: guessAdType(ad.title),
            duration: 30,
            startDate: ad.updated_at || ad.created_at || new Date().toISOString(),
            active: ad.active !== 0
          }));

        setAds(normalized.length > 0 ? normalized : DEFAULT_ADS);
        return;
      }

      setAds(DEFAULT_ADS);
    } catch (error) {
      console.error('[AdvertisementBanner] Failed to load ads:', error);
      setAds(DEFAULT_ADS);
    }
  }, []);

  useEffect(() => {
    loadAdsFromDatabase();

    const handleFocus = () => {
      loadAdsFromDatabase();
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === USER_ADS_KEY) {
        setUserAdsEnabled(event.newValue !== 'false');
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorage);
    };
  }, [loadAdsFromDatabase]);

  // Rotate ads every 10 seconds
  useEffect(() => {
    if (!adsAreVisible) return;

    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % ads.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [adsAreVisible, ads.length]);

  const toggleAds = () => {
    const newState = !userAdsEnabled;
    setUserAdsEnabled(newState);
    localStorage.setItem(USER_ADS_KEY, String(newState));
  };

  if (!globalAdsEnabled || ads.length === 0) {
    return null;
  }

  if (!userAdsEnabled) {
    return (
      <button
        onClick={toggleAds}
        className="fixed bottom-4 right-4 z-30 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl border border-slate-700 transition-all text-sm flex items-center gap-2"
      >
        <EyeOff className="w-4 h-4" />
        <span>Show Ads</span>
      </button>
    );
  }

  const currentAd = ads[currentAdIndex];

  const getColorByType = (type: string) => {
    switch (type) {
      case 'bbq':
        return 'from-purple-600 to-pink-600';
      case 'school':
        return 'from-blue-600 to-cyan-600';
      case 'business':
        return 'from-green-600 to-emerald-600';
      default:
        return 'from-slate-600 to-slate-700';
    }
  };

  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className={`fixed bottom-4 right-4 z-30 px-4 py-2 bg-gradient-to-r ${getColorByType(currentAd.type)} text-white rounded-xl shadow-lg hover:shadow-xl transition-all text-sm font-semibold`}
      >
        📢 View Ad ({currentAdIndex + 1}/{ads.length})
      </button>
    );
  }

  return (
    <>
      <div className="fixed bottom-4 right-4 z-30 w-80 max-w-[calc(100vw-2rem)]">
        <div className={`bg-gradient-to-r ${getColorByType(currentAd.type)} rounded-2xl p-4 shadow-2xl border-2 border-white/20`}>
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-white/70 mb-1">
                Advertisement {currentAdIndex + 1}/{ads.length}
              </p>
              <h3 className="text-sm font-bold text-white">{currentAd.title}</h3>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setIsCollapsed(true)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                title="Minimize"
              >
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          </div>

        {/* Content */}
        <p className="text-sm text-white/90 mb-3">{currentAd.content}</p>

        {/* Actions */}
        <div className="flex gap-2">
          {currentAd.link && currentAd.link !== '#' && (
            <a
              href={currentAd.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-3 py-2 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold rounded-lg transition-colors text-center"
            >
              Learn More
            </a>
          )}
          <button
            onClick={toggleAds}
            className="px-3 py-2 bg-black/20 hover:bg-black/30 text-white text-xs rounded-lg transition-colors flex items-center gap-1"
            title="Disable ads"
          >
            <Eye className="w-3 h-3" />
            <span>Hide Ads</span>
          </button>
        </div>

        {/* Indicators */}
        {ads.length > 1 && (
          <div className="flex justify-center gap-1 mt-3">
            {ads.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentAdIndex(index)}
                className={`h-1.5 rounded-full transition-all ${
                  index === currentAdIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/40'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}
